import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { useCurrentIdentity } from '@/lib/identity-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DriftSeverityBadge } from '@/components/baseline-badge';
import { DiffReport } from '@/components/diff-report';
import { ApproveBaselineModal } from '@/components/approve-baseline-modal';
import { computeDriftSeverity, hasAnyDrift } from '@/lib/drift-severity';
import { formatDateTime } from '@/lib/format';

export const siteBaselineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/$siteId/baseline',
  component: BaselinePage,
});

function BaselinePage() {
  const { siteId } = siteBaselineRoute.useParams();
  const { hasPermission } = useCurrentIdentity();
  const canApprove = hasPermission('baseline:approve');
  const [showApprove, setShowApprove] = useState(false);

  const baselineQuery = useQuery({
    queryKey: queryKeys.sites.baseline(siteId),
    retry: false,
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET('/sites/{siteId}/baseline', {
        params: { path: { siteId } },
      });
      if (error) {
        if (response?.status === 404) return null;
        throw error;
      }
      return data;
    },
  });

  const driftQuery = useQuery({
    queryKey: queryKeys.sites.drift(siteId),
    retry: false,
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET('/sites/{siteId}/drift', {
        params: { path: { siteId } },
      });
      if (error) {
        if (response?.status === 404) return null;
        throw error;
      }
      return data;
    },
  });

  const historyQuery = useQuery({
    queryKey: ['sites', siteId, 'baseline', 'history'] as const,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}/baseline/history', {
        params: { path: { siteId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const crawlsQuery = useQuery({
    queryKey: queryKeys.sites.crawls(siteId, 1),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}/crawls', {
        params: { path: { siteId }, query: { page: 1, pageSize: 10 } },
      });
      if (error) throw error;
      return data;
    },
  });

  const baseline = baselineQuery.data ?? null;
  const drift = driftQuery.data ?? null;
  const severity = drift ? computeDriftSeverity(drift) : null;
  const latestCompleted = (crawlsQuery.data?.items ?? []).find((c) => c.status === 'completed');
  const history = historyQuery.data?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link
          to="/sites/$siteId"
          params={{ siteId }}
          className="text-xs text-muted-foreground hover:underline"
          data-testid="site-baseline-back-link"
        >
          ← Back to site
        </Link>
      </div>
      <Card data-testid="baseline-current-card">
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Current baseline</CardTitle>
            {baseline ? (
              <p className="text-xs text-muted-foreground">
                Approved {formatDateTime(baseline.approvedAt)} by{' '}
                {baseline.approvedBy?.email ?? 'system'} · crawl{' '}
                <span className="font-mono">{baseline.crawlId.slice(0, 12)}…</span>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">No baseline approved yet.</p>
            )}
          </div>
          {canApprove && latestCompleted && (
            <Button
              size="sm"
              onClick={() => setShowApprove(true)}
              data-testid="approve-baseline-button"
            >
              Approve current crawl as baseline
            </Button>
          )}
        </CardHeader>
        {baseline?.notes && (
          <CardContent>
            <div className="rounded-sm border bg-muted/30 p-2 text-xs">{baseline.notes}</div>
          </CardContent>
        )}
      </Card>

      {drift && baseline && (
        <Card data-testid="baseline-drift-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle>Drift vs baseline</CardTitle>
              <DriftSeverityBadge severity={severity} />
              {!hasAnyDrift(drift) && <Badge variant="success">In sync</Badge>}
            </div>
          </CardHeader>
          <CardContent>
            <DiffReport report={drift} earlierLabel="baseline" laterLabel="latest crawl" />
          </CardContent>
        </Card>
      )}

      <Card data-testid="baseline-history-card">
        <CardHeader>
          <CardTitle>Baseline history</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No baselines recorded yet.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {history.map((b) => (
                <li key={b.id} className="flex items-center justify-between gap-2 rounded-sm border bg-muted/30 p-2 text-xs">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-mono">{b.crawlId.slice(0, 12)}…</span>
                    <span className="text-muted-foreground">
                      approved {formatDateTime(b.approvedAt)} by {b.approvedBy?.email ?? 'system'}
                    </span>
                  </div>
                  {b.supersededAt ? (
                    <span className="text-muted-foreground">superseded {formatDateTime(b.supersededAt)}</span>
                  ) : (
                    <Badge variant="success">Active</Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {showApprove && latestCompleted && (
        <ApproveBaselineModal
          siteId={siteId}
          candidateCrawlId={latestCompleted.id}
          onClose={() => setShowApprove(false)}
        />
      )}
    </div>
  );
}
